import AsyncCacheManager from '../AsyncCacheManager';
import type { KeyBuildResult } from '../modules/keyBuilder';

/**
 * Cache Registry
 *
 * Purpose
 * - Deduplicate AsyncCacheManager instances across the app by (namespace, group) tuple.
 * - Ensure shared configuration and DB connections for identical scopes.
 *
 * Contract
 * - getCache({ namespace, group, ...defaults }): returns a shared AsyncCacheManager.
 * - Instances are memoized by key = `${namespace}::${group}`.
 */

export interface RegistryOptions {
  /** Optional namespace segment used to prefix keys */
  namespace?: string;
  /** Optional group segment used to prefix keys */
  group?: string;
  /** Default TTL for entries (ms) */
  defaultTtlMs?: number;
  /** Default TTL for entries in human string form e.g. '5m' */
  defaultTtl?: string;
  /** Fallback max bytes when StorageManager.estimate is unavailable */
  maxBytesFallback?: number;
  /** Fraction of entries to evict when over capacity (0.1..0.9) */
  evictFraction?: number;
  /** Max usage ratio vs quota when estimates are available (0.5..0.95) */
  maxUsageRatio?: number;
  /** Enable hashing for large param/body key segments to avoid long keys. */
  hashLargeSegments?: boolean;
  /** Length threshold beyond which segments will be hashed (default 256). */
  hashThreshold?: number;
  /** When true, store sidecar key meta for UI search/inspection. */
  emitMeta?: boolean;
}

/**
 * Internal registry map, keyed by `${namespace}::${group}`.
 */
const REGISTRY: Map<string, AsyncCacheManager> = new Map();

/**
 * Build a stable registry key.
 * @param ns Optional namespace
 * @param grp Optional group
 */
function registryKey(ns?: string, grp?: string): string {
  return `${ns ?? ''}::${grp ?? ''}`;
}

/**
 * Retrieve a shared AsyncCacheManager instance for the given scope, creating it once.
 *
 * @param opts Registry options including namespace/group and default cache behaviors
 * @returns Shared AsyncCacheManager for that scope
 */
export function getCache(opts: RegistryOptions = {}): AsyncCacheManager {
  const key = registryKey(opts.namespace, opts.group);
  const existing = REGISTRY.get(key);
  if (existing) return existing;
  const created = new AsyncCacheManager(opts);
  REGISTRY.set(key, created);
  return created;
}

/**
 * Clear the registry (primarily for tests or hot module reload scenarios).
 */
export function resetRegistry(): void {
  REGISTRY.clear();
}

/**
 * List stored sidecar meta entries for a given namespace/group scope.
 * Returns the full meta (KeyBuildResult) so UIs can search/dehash.
 */
export async function listCacheMeta(
  scope: { namespace?: string; group?: string }
): Promise<Array<{ key: string; meta: KeyBuildResult }>> {
  const db = await AsyncCacheManager.openSharedDb();
  const tx = db.transaction('meta', 'readonly');
  const store = tx.objectStore('meta');
  const out: Array<{ key: string; meta: KeyBuildResult }> = [];
  const prefixParts = [scope.namespace, scope.group].filter((v) => v != null && String(v).length > 0);
  const prefix = prefixParts.length ? prefixParts.join(':') + ':' : '';

  await new Promise<void>((resolve, reject) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result as IDBCursorWithValue | null;
      if (cursor) {
        const rec = cursor.value as { key: string; meta: KeyBuildResult };
        if (!prefix || rec.key.startsWith(prefix)) {
          out.push({ key: rec.key, meta: rec.meta });
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
    req.onerror = () => resolve();
  });
  return out;
}

const Registry = { getCache, resetRegistry };

export default Registry;
